<template>
  <v-row>
    <v-col cols="12">
      <v-card>
        <v-card-title>
          Free Trials Management / إدارة الفترات التجريبية
          <v-spacer></v-spacer>
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Search Schools... / البحث عن مدرسة..."
            single-line
            hide-details
            @input="onSearch"
          ></v-text-field>
        </v-card-title>

        <v-data-table
          :headers="headers"
          :items="schools"
          :loading="loading"
          item-key="id"
          :page.sync="page"
          :items-per-page.sync="itemsPerPage"
          :server-items-length="totalItems"
          :footer-props="{
            'items-per-page-options': [5, 10, 25, 50]
          }"
          class="elevation-1"
          @update:page="onPageChange"
          @update:items-per-page="onItemsPerPageChange"
        >
          <template #item.free_trial="{ item }">
            <v-switch
              :input-value="!!item.free_trial"
              @change="val => onToggleSwitch(item, val)"
              :loading="item.__saving === true"
              inset
              color="success"
            ></v-switch>
          </template>

          <template #item.free_trial_status="{ item }">
            <v-chip
              small
              :color="statusChipColor(item.free_trial_status)"
              text-color="white"
            >
              {{ statusLabelText(item.free_trial_status) }}
            </v-chip>
          </template>

          <template #item.free_trial_ends_at="{ item }">
            <div>
              <div>{{ formatEndAt(item) }}</div>
              <div v-if="item.free_trial && item.days_left !== undefined" class="caption grey--text">
                ({{ item.days_left }} days remaining / متبقي {{ item.days_left }} يوم)
              </div>
            </div>
          </template>

          <template #item.actions="{ item }">
            <v-btn
              icon
              small
              color="primary"
              title="Edit Duration / تعديل المدة"
              @click="openDurationDialog(item)"
            >
              <v-icon small>mdi-clock-edit-outline</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card>
    </v-col>

    <!-- Trial Duration Dialog -->
    <v-dialog v-model="dialog" max-width="500px" persistent>
      <v-card v-if="selectedSchool">
        <v-card-title class="headline primary white--text">
          <v-icon left color="white">mdi-clock-fast</v-icon>
          Free Trial Duration / مدة التجربة المجانية
        </v-card-title>

        <v-card-text class="pt-4">
          <div class="subtitle-1 font-weight-bold mb-2">
            School: {{ selectedSchool.name }}
          </div>
          <div class="body-2 mb-4 text--secondary">
            Set or extend the free trial duration in days. Disabling trial will automatically lock routes, driver trips, and notifications.
          </div>

          <!-- Quick Presets -->
          <div class="mb-3">
            <span class="caption font-weight-bold grey--text text--darken-1 d-block mb-1">Quick Presets / اختيارات سريعة:</span>
            <v-btn-toggle v-model="presetIndex" mandatory dense color="primary">
              <v-btn small @click="selectPreset(7)">7 Days</v-btn>
              <v-btn small @click="selectPreset(15)">15 Days</v-btn>
              <v-btn small @click="selectPreset(30)">30 Days</v-btn>
              <v-btn small @click="selectPreset(60)">60 Days</v-btn>
              <v-btn small @click="selectPreset(90)">90 Days</v-btn>
            </v-btn-toggle>
          </div>

          <!-- Custom Days Input -->
          <v-text-field
            v-model.number="trialDays"
            label="Duration (in Days) / المدة بالأيام"
            type="number"
            min="1"
            max="365"
            outlined
            dense
            prepend-inner-icon="mdi-calendar-range"
            hint="Enter exact number of days"
            persistent-hint
          ></v-text-field>
        </v-card-text>

        <v-card-actions class="px-4 pb-4">
          <v-btn color="error" text @click="disableTrialForSelected">
            <v-icon left small>mdi-power</v-icon> Disable Trial
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn text @click="closeDurationDialog">Cancel</v-btn>
          <v-btn color="primary" :loading="savingDialog" @click="saveDuration">
            <v-icon left small>mdi-check</v-icon> Save & Activate
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import FreeTrialService from '@/services/FreeTrialService.js'

export default {
  data() {
    return {
      loading: false,
      savingDialog: false,
      schools: [],
      search: '',
      page: 1,
      itemsPerPage: 10,
      totalItems: 0,
      dialog: false,
      selectedSchool: null,
      trialDays: 30,
      presetIndex: 2,
    };
  },
  computed: {
    headers() {
      return [
        { text: 'ID', value: 'id' },
        { text: 'School Name / اسم المدرسة', value: 'name' },
        { text: 'Email / البريد الإلكتروني', value: 'email' },
        { text: 'Free Trial / التجربة المجانية', value: 'free_trial', sortable: false },
        { text: 'Status / الحالة', value: 'free_trial_status' },
        { text: 'Ends At / تاريخ الانتهاء', value: 'free_trial_ends_at' },
        { text: 'Actions / الإجراءات', value: 'actions', sortable: false, align: 'center' },
      ]
    },
  },
  mounted() {
    this.fetchSchools();
  },
  methods: {
    statusLabelText(status) {
      if (status === 'active') return 'Active / مفعل'
      if (status === 'expired') return 'Expired / منتهي'
      return 'Off / معطل'
    },
    statusChipColor(status) {
      if (status === 'active') return 'success'
      if (status === 'expired') return 'warning'
      return 'grey'
    },
    formatEndAt(item) {
      const iso = item.free_trial_ends_at
      if (!iso) {
        return '—'
      }
      try {
        const d = new Date(iso)
        if (Number.isNaN(d.getTime())) {
          return '—'
        }
        return d.toLocaleString()
      } catch (e) {
        return '—'
      }
    },
    async fetchSchools() {
      this.loading = true;
      try {
        const response = await FreeTrialService.getSchoolsFreeTrial(this.page, this.itemsPerPage);
        if (response && response.success) {
          this.schools = (response.data || []).map(s => ({
            ...s,
            __saving: false,
            free_trial_status: s.free_trial_status || 'off',
          }));
          this.totalItems = response.total || 0;
        } else {
          this.$notify({ title: 'Error', text: 'Failed to load schools', type: 'error' });
        }
      } catch (error) {
        this.$notify({ title: 'Error', text: 'Failed to load schools', type: 'error' });
      } finally {
        this.loading = false;
      }
    },
    onPageChange(page) {
      this.page = page;
      this.fetchSchools();
    },
    onItemsPerPageChange(itemsPerPage) {
      this.itemsPerPage = itemsPerPage;
      this.page = 1;
      this.fetchSchools();
    },
    onSearch() {
      this.page = 1;
    },
    onToggleSwitch(item, value) {
      if (value) {
        this.openDurationDialog(item);
      } else {
        this.disableTrial(item);
      }
    },
    openDurationDialog(item) {
      this.selectedSchool = item;
      this.trialDays = item.days_left && item.days_left > 0 ? item.days_left : 30;
      this.dialog = true;
    },
    closeDurationDialog() {
      this.dialog = false;
      this.selectedSchool = null;
    },
    selectPreset(days) {
      this.trialDays = days;
    },
    async saveDuration() {
      if (!this.selectedSchool) return;
      this.savingDialog = true;
      const school = this.selectedSchool;
      const days = this.trialDays || 30;

      try {
        const response = await FreeTrialService.setSchoolFreeTrial(school.id, true, days);
        if (response && response.success && response.data) {
          const d = response.data;
          school.free_trial = true;
          if (d.free_trial_started_at !== undefined) school.free_trial_started_at = d.free_trial_started_at;
          if (d.free_trial_ends_at !== undefined) school.free_trial_ends_at = d.free_trial_ends_at;
          if (d.free_trial_status !== undefined) school.free_trial_status = d.free_trial_status;
          if (d.days_left !== undefined) school.days_left = d.days_left;

          this.$notify({ title: 'Saved', text: `Trial updated to ${days} days`, type: 'success' });
          this.closeDurationDialog();
        } else {
          throw new Error('Save failed');
        }
      } catch (error) {
        this.$notify({ title: 'Error', text: 'Failed to update trial duration', type: 'error' });
      } finally {
        this.savingDialog = false;
      }
    },
    async disableTrialForSelected() {
      if (!this.selectedSchool) return;
      await this.disableTrial(this.selectedSchool);
      this.closeDurationDialog();
    },
    async disableTrial(item) {
      item.__saving = true;
      try {
        const response = await FreeTrialService.unsetSchoolFreeTrial(item.id);
        if (response && response.success && response.data) {
          const d = response.data;
          item.free_trial = false;
          if (d.free_trial_ends_at !== undefined) item.free_trial_ends_at = d.free_trial_ends_at;
          if (d.free_trial_status !== undefined) item.free_trial_status = d.free_trial_status;
          item.days_left = 0;
          this.$notify({ title: 'Disabled', text: 'Free trial disabled successfully', type: 'success' });
        } else {
          throw new Error('Disable failed');
        }
      } catch (error) {
        item.free_trial = true;
        this.$notify({ title: 'Error', text: 'Failed to disable trial', type: 'error' });
      } finally {
        item.__saving = false;
      }
    },
  },
};
</script>

<style scoped>
</style>
